import { decideBotIntent } from "./bot-ai.ts";
import { arenaBounds, ELIMINATION_SCORE, isShotOutOfArena, resolveShotHit } from "./combat.ts";
import { updateBounty } from "./bounty.ts";
import { activateBoost, activateShield, addSparks, advanceOutboundShot, hasReachedShotRange, launchShot } from "./controller.ts";
import { applyStorm, getStormCenter, STORM_COLOR } from "./storm.ts";
import {
  clamp,
  createBot,
  createEnergy,
  distance,
  energyOwnerPool,
  findParticipant,
  type GameData,
  type Participant,
  type ParticipantId,
} from "./model.ts";
import { getOrbitPosition, orbitPhaseFor } from "./orbit.ts";
import { recycleInactiveShots, recycleInactiveSparks } from "./pool.ts";
import { chargeOf, getCollisionRadius, getShieldRadius, surgeWait, type GameSettings } from "./settings.ts";



/** Everything one participant wants to do during a tick. */
export type PlayerInput = {
  /** Desired direction; the caller may leave it at zero and steer by aim instead. */
  moveX: number;
  moveY: number;
  /** Aim target in world space. */
  aimX: number;
  aimY: number;
  aiming: boolean;
  attack: boolean;
  boost: boolean;
  shield: boolean;
  /**
   * Set by an authoritative server when a participant has no input queued for this
   * tick. Movement is skipped entirely rather than replaying the previous frame:
   * the client predicts exactly one step per frame it sends, so an unrequested
   * extra step is a step it can never reconcile — one step of drift, arriving
   * whenever the buffer ran dry.
   */
  hold?: boolean;
};

export const NEUTRAL_INPUT: PlayerInput = {
  moveX: 0, moveY: 0, aimX: 0, aimY: 0, aiming: false, attack: false, boost: false, shield: false,
};

/** Inputs for this tick, keyed by participant id. Missing ids act neutrally. */
export type InputMap = Record<ParticipantId, PlayerInput>;

/**
 * Anything the simulation wants the presentation layer to know about. Events carry
 * ids rather than object references so they serialise straight onto the wire.
 */
/**
 * Combat events carry where they happened and what they paid.
 *
 * The client used to be told only that a steal had occurred, so all it could do
 * was write a line in the corner — the player learned the score had changed by
 * watching a number change. With the impact point and the amount, the same event
 * can put the figure on the spot the orb landed, which is the only place a player
 * is looking during a fight.
 */
type Impact = { x: number; y: number; score: number };

export type GameEvent =
  | ({ type: "pickup-own"; participant: ParticipantId } & Impact)
  | ({ type: "pickup-enemy"; participant: ParticipantId } & Impact)
  | { type: "shot-fired"; participant: ParticipantId }
  | { type: "surge-armed"; participant: ParticipantId; tier: number }
  | { type: "boost-spent"; participant: ParticipantId }
  | { type: "shield-raised"; participant: ParticipantId }
  | ({ type: "shield-capture"; participant: ParticipantId } & Impact)
  | ({ type: "steal"; attacker: ParticipantId; victim: ParticipantId; victimName: string; attackerName: string; attackerColor: string; victimColor: string; taken: number } & Impact)
  | ({ type: "eliminate"; attacker: ParticipantId; victim: ParticipantId; victimName: string; attackerName: string; attackerColor: string; victimColor: string; taken: number } & Impact)
  /**
   * Who the room is hunting now, or null when the mark has come off. Sent to
   * everyone, not just the two ends of it: the whole point is that it is public.
   */
  | { type: "bounty"; participant: ParticipantId | null; name: string }
  /** The storm is coming, or has opened, and where. Sent to the whole room. */
  | { type: "storm"; phase: "warning" | "open"; x: number; y: number; seconds: number }
  | { type: "storm-orb"; participant: ParticipantId; added: number }
  | { type: "time-up" };

export const PLAYER_BASE_SPEED = 170;
export const PLAYER_STEERING = 7;
/**
 * A bot's body, as a fraction of a person's, dialled by botSkill.
 *
 * They used to be nailed to 68px/s against a player's 139-170, and to a third of
 * the turn rate — half speed and a third of the agility. No decision an AI makes
 * can threaten somebody it cannot catch and cannot escape, so the brain was never
 * the thing holding them back. At the default dial they are a notch below a person;
 * at 1 they keep up.
 */
export function botSpeedFor(settings: Pick<GameSettings, "botSkill">, participant: Pick<Participant, "orbs">) {
  return loadedSpeed(participant.orbs) * (0.55 + settings.botSkill * 0.45);
}

export function botSteeringFor(settings: Pick<GameSettings, "botSkill">) {
  return PLAYER_STEERING * (0.45 + settings.botSkill * 0.55);
}

export function botPickupRangeFor(settings: Pick<GameSettings, "botSkill">) {
  return HUMAN_PICKUP_RANGE * (0.7 + settings.botSkill * 0.3);
}

/** Carrying orbs slows a core down — for everyone, not just for people. */
function loadedSpeed(orbs: number) {
  return PLAYER_BASE_SPEED - Math.min(orbs, 14) * 2.2;
}
/** Distance below which pointer steering stops, so the core does not jitter. */
export const HUMAN_PICKUP_RANGE = 31;

function inputFor(inputs: InputMap, id: ParticipantId): PlayerInput {
  return inputs[id] ?? NEUTRAL_INPUT;
}

/** Turns an AI decision into the same input shape a human sends over the wire. */
function botInput(game: GameData, bot: Participant, settings: GameSettings): PlayerInput {
  const intent = decideBotIntent(game, bot, settings);
  bot.targetX = clamp(intent.moveX, 0, game.width);
  bot.targetY = clamp(intent.moveY, 70, game.height);
  const dx = bot.targetX - bot.x;
  const dy = bot.targetY - bot.y;
  const length = Math.max(1, Math.hypot(dx, dy));
  return {
    moveX: dx / length,
    moveY: dy / length,
    aimX: intent.fireX ?? bot.x,
    aimY: intent.fireY ?? bot.y,
    aiming: intent.fireX !== null && intent.fireY !== null,
    attack: intent.fireX !== null && intent.fireY !== null,
    boost: intent.useBoost,
    shield: intent.useShield,
  };
}

/**
 * Advances the whole simulation by `dt` seconds and reports what happened. Pure with
 * respect to the outside world: the only things it mutates are `game` and the objects
 * it owns, which is what lets an authoritative server run exactly this code.
 */
export function stepGame(
  game: GameData,
  inputs: InputMap,
  dt: number,
  settings: GameSettings,
): GameEvent[] {
  const events: GameEvent[] = [];
  if (!game.running) return events;

  game.elapsed = (Number.isFinite(game.elapsed) ? game.elapsed : 0) + dt;
  // A match length of 0 is an arena that never ends: no countdown, no time-up.
  if (game.matchDuration > 0) game.timeLeft = Math.max(0, game.timeLeft - dt);

  // Bots decide first so their inputs join the same pipeline as the humans'.
  const resolved: InputMap = { ...inputs };
  for (const participant of game.participants) {
    if (participant.kind !== "bot" || !participant.alive) continue;
    resolved[participant.id] = botInput(game, participant, settings);
  }

  for (const participant of game.participants) {
    stepParticipant(game, participant, inputFor(resolved, participant.id), dt, settings, events);
  }

  if (game.matchDuration > 0 && game.timeLeft <= 0) {
    game.running = false;
    events.push({ type: "time-up" });
  }

  stepStorm(game, settings, dt, events);
  stepShots(game, dt, settings, events);
  stepSparks(game, dt);

  // After the shots, so a core that has just been broken cannot keep the mark for
  // the rest of the tick, and a kill that puts someone in front is reflected now.
  const marked = updateBounty(game, dt);
  if (marked !== undefined) {
    const hunted = marked === null ? null : findParticipant(game, marked);
    events.push({ type: "bounty", participant: marked, name: hunted?.name ?? "" });
  }

  return events;
}

function stepParticipant(
  game: GameData,
  participant: Participant,
  input: PlayerInput,
  dt: number,
  settings: GameSettings,
  events: GameEvent[],
) {
  if (!participant.alive) {
    if (participant.kind !== "bot") return;
    participant.respawn -= dt;
    if (participant.respawn <= 0) {
      const index = Number(participant.id.split(":")[1] ?? 1) - 1;
      // Respawning must not steal a colour someone else now holds, so everyone
      // else's colour is off the table — including the seat's own previous one,
      // which allocateBotColor will hand straight back if it is still free.
      const taken = game.participants
        .filter((other) => other.id !== participant.id)
        .map((other) => other.color);
      Object.assign(participant, createBot(index, game.width, game.height, settings.fireInterval, game.random, taken));
    }
    return;
  }

  participant.spawnProtection = Math.max(0, participant.spawnProtection - dt);
  participant.surgeCooldown = Math.max(0, participant.surgeCooldown - dt);
  participant.shield = Math.max(0, participant.shield - dt);
  participant.shieldCooldown = Math.max(0, participant.shieldCooldown - dt);
  participant.attackCooldown = Math.max(0, participant.attackCooldown - dt);
  participant.boostRemaining = Math.max(0, participant.boostRemaining - dt);

  if (input.attack) {
    // The sphere that leaves is the one the player can see leaving: same phase
    // function, same simulation clock, both ends.
    const origin = getOrbitPosition(
      participant.x,
      participant.y,
      Math.max(0, participant.orbs - 1),
      orbitPhaseFor(participant.id, game.lastTime / 1000),
      settings,
    );
    const aim = { x: input.aimX, y: input.aimY, active: input.aiming };
    const charge = chargeOf(participant);
    if (launchShot(game, participant, aim, settings.shotSpeed, settings.fireInterval, origin, charge, surgeWait(charge, settings))) {
      // Taking a shot ends the grace period: immunity is there to let you get out
      // of a bad spawn, not to hunt from behind it.
      participant.spawnProtection = 0;
      events.push({ type: "shot-fired", participant: participant.id });
    }
  }
  if (input.boost && activateBoost(game, participant, settings.boostDuration)) {
    events.push({ type: "boost-spent", participant: participant.id });
  }
  if (input.shield && activateShield(game, participant)) {
    events.push({ type: "shield-raised", participant: participant.id });
  }

  stepMovement(game, participant, input, dt, settings);
  stepPickups(game, participant, settings, events);
}

/**
 * Moves one participant for `dt`. Exported because the client predicts its own core
 * with this exact function: prediction that disagrees with the server's physics
 * would drift and need constant correcting.
 */
export function stepMovement(game: GameData, participant: Participant, input: PlayerInput, dt: number, settings: GameSettings) {
  if (input.hold) return;
  const isBot = participant.kind === "bot";
  // Movement is the move vector and nothing else. The pointer used to double as a
  // steering stick whenever no key was down, so aiming at something was also a
  // decision to travel towards it — you could not hold a line and line up a shot at
  // the same time, because both were the same hand on the same device. Bots have
  // always supplied a direction of their own.
  const moveX = input.moveX;
  const moveY = input.moveY;

  const moveLength = Math.max(1, Math.hypot(moveX, moveY));
  const boosting = participant.boostRemaining > 0;
  const base = isBot ? botSpeedFor(settings, participant) : loadedSpeed(participant.orbs);
  const speed = base * (boosting ? settings.boostMultiplier : 1);
  const steering = (isBot ? botSteeringFor(settings) : PLAYER_STEERING) * (boosting ? settings.boostTurnControl : 1);
  participant.vx += ((moveX / moveLength) * speed - participant.vx) * Math.min(1, dt * steering);
  participant.vy += ((moveY / moveLength) * speed - participant.vy) * Math.min(1, dt * steering);
  if (!isBot && moveX === 0 && moveY === 0) {
    participant.vx *= Math.pow(0.02, dt);
    participant.vy *= Math.pow(0.02, dt);
  }

  // Bounded by the core, not by the orbit: fencing the whole orbit meant the more
  // orbs you carried the further from the wall you were held.
  const wall = arenaBounds(game, settings.coreRadius);
  participant.x = clamp(participant.x + participant.vx * dt, wall.minX, wall.maxX);
  participant.y = clamp(participant.y + participant.vy * dt, wall.minY, wall.maxY);
}

function stepPickups(game: GameData, participant: Participant, settings: GameSettings, events: GameEvent[]) {
  const isBot = participant.kind === "bot";
  const range = isBot ? botPickupRangeFor(settings) : HUMAN_PICKUP_RANGE;
  for (const item of game.energy) {
    if (distance(participant.x, participant.y, item.x, item.y) >= range) continue;
    if (item.owner === participant.id) {
      const before = participant.orbs;
      participant.orbs = Math.min(settings.maxOrbs, participant.orbs + 1);
      participant.score += settings.ownEnergyScore;
      if (!isBot) {
        events.push({ type: "pickup-own", participant: participant.id, x: item.x, y: item.y, score: settings.ownEnergyScore });
      }
      if (chargeOf(participant) > chargeOf({ ...participant, orbs: before })) {
        events.push({ type: "surge-armed", participant: participant.id, tier: chargeOf(participant) });
      }
    } else {
      participant.score += settings.enemyEnergyScore;
      if (!isBot) {
        events.push({ type: "pickup-enemy", participant: participant.id, x: item.x, y: item.y, score: settings.enemyEnergyScore });
      }
    }
    if (!isBot) addSparks(game, item.x, item.y, item.color, 8);
    // A collected particle respawns elsewhere **in the same colour**. Re-rolling the
    // owner would let one core's share drift away over a match, and "collect your
    // own" only works if your share of the field stays constant.
    const owner = findParticipant(game, item.owner);
    const pool = owner
      ? [{ id: owner.id, color: owner.color }]
      : energyOwnerPool(game);
    Object.assign(item, createEnergy(game.nextId++, game.width, game.height, pool, game.random));
    // Bots take one pickup per tick; a human sweeping a cluster may take several.
    if (isBot) break;
  }
}

function stepStorm(game: GameData, settings: GameSettings, dt: number, events: GameEvent[]) {
  const result = applyStorm(game, settings, dt);
  // Two announcements, not one. The warning is the one that matters — it is what
  // gets everybody moving to the same place at the same time — and the opening is
  // what tells whoever arrived that it is worth standing in now.
  const phase = result.state.active ? "open" : result.state.warning ? "warning" : "closed";
  if (phase !== game.stormPhase) {
    game.stormPhase = phase;
    if (phase !== "closed") {
      const centre = getStormCenter(result.state.cycle, game.width, game.height, settings.stationRadius);
      events.push({
        type: "storm",
        phase,
        x: Math.round(centre.x),
        y: Math.round(centre.y),
        seconds: Math.ceil(phase === "warning" ? result.state.nextActivation : result.state.activeRemaining),
      });
    }
  }
  game.stationsWereActive = result.state.active;

  for (const gain of result.collected) {
    const character = findParticipant(game, gain.owner);
    if (!character) continue;
    addSparks(game, character.x, character.y, STORM_COLOR, character.kind === "human" ? 18 : 10);
    events.push({ type: "storm-orb", participant: gain.owner, added: gain.added });
  }
}

function stepShots(game: GameData, dt: number, settings: GameSettings, events: GameEvent[]) {
  for (const shot of game.shots) {
    if (!shot.active) continue;
    shot.life += dt;
    const owner = findParticipant(game, shot.owner);
    if (!owner?.alive) {
      shot.active = false;
      continue;
    }

    if (shot.returning) {
      const dx = owner.x - shot.x;
      const dy = owner.y - shot.y;
      const length = Math.max(1, Math.hypot(dx, dy));
      shot.vx = (dx / length) * settings.returnSpeed;
      shot.vy = (dy / length) * settings.returnSpeed;
      shot.x += shot.vx * dt;
      shot.y += shot.vy * dt;
      // Caught at the core's own size. A fixed 25px meant a wide core swallowed its
      // orb late — or, with the widest settings, flew past and circled back.
      if (length < settings.coreRadius + settings.orbRadius) {
        const before = owner.orbs;
        owner.orbs = Math.min(settings.maxOrbs, owner.orbs + 1 + (shot.stolen ? shot.taken : 0));
        if (chargeOf(owner) > chargeOf({ ...owner, orbs: before })) {
          events.push({ type: "surge-armed", participant: owner.id, tier: chargeOf(owner) });
        }
        addSparks(game, owner.x, owner.y, shot.stolen ? "#65f3ff" : owner.color, shot.stolen ? 16 : 8);
        shot.active = false;
      }
      continue;
    }

    advanceOutboundShot(shot, dt);
    // Whether the orb has run out of road is decided *after* asking whether it hit
    // something. An orb expiring one pixel inside a core is a miss no player can
    // explain, and at 450px/s that tick is 7px wide.
    const spent = hasReachedShotRange(shot, settings.shotRange) || isShotOutOfArena(shot, game);

    for (const victim of game.participants) {
      if (victim.id === owner.id || !victim.alive) continue;
      // A freshly spawned core is passed straight through, not blocked: the shot
      // keeps travelling so it can still reach whoever is behind them.
      if (victim.spawnProtection > 0) continue;
      const shielded = victim.shield > 0;
      const radius = shielded ? getShieldRadius(settings) : getCollisionRadius(victim.orbs, settings);
      if (distance(shot.x, shot.y, victim.x, victim.y) > radius) continue;

      if (shielded) {
        victim.orbs = Math.min(settings.maxOrbs, victim.orbs + 1);
        victim.score += settings.shieldCaptureScore;
        addSparks(game, shot.x, shot.y, victim.color, 18);
        shot.active = false;
        events.push({
          type: "shield-capture",
          participant: victim.id,
          x: shot.x,
          y: shot.y,
          score: settings.shieldCaptureScore,
        });
        break;
      }

      // Read before resolving: the shot is turned around and the victim's orbs are
      // taken by the time it returns, so this is the last moment the impact point
      // and the score the attacker was on are still the ones that belong together.
      const impactX = shot.x;
      const impactY = shot.y;
      const scoreBefore = owner.score;
      const outcome = resolveShotHit(game, shot, owner, victim, settings);
      events.push({
        type: outcome.kind,
        attacker: owner.id,
        victim: victim.id,
        victimName: victim.name,
        attackerName: owner.name,
        attackerColor: owner.color,
        victimColor: victim.color,
        taken: outcome.taken ?? 0,
        x: impactX,
        y: impactY,
        score: owner.score - scoreBefore,
      });
      break;
    }

    // `resolveShotHit` turns a landed orb around, so only an orb that touched
    // nothing is spent.
    if (spent && !shot.returning) shot.active = false;
  }

  recycleInactiveShots(game);
}

function stepSparks(game: GameData, dt: number) {
  for (const spark of game.sparks) {
    if (!spark.active) continue;
    spark.x += spark.vx * dt;
    spark.y += spark.vy * dt;
    spark.vx *= Math.pow(0.08, dt);
    spark.vy *= Math.pow(0.08, dt);
    spark.life -= dt;
    if (spark.life <= 0) spark.active = false;
  }
  recycleInactiveSparks(game);
}

export { ELIMINATION_SCORE };
